import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Upload, RotateCcw, Plus, Trash2, Edit2 } from 'lucide-react';
import '../styles/DestinationsEditor.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DestinationsEditor = ({ token }) => {
  const [destinations, setDestinations] = useState([]);
  const [attributes, setAttributes] = useState({});
  const [versions, setVersions] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);

  useEffect(() => {
    fetchDestinations();
    fetchAttributes();
    fetchVersions();
  }, []);

  const fetchDestinations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/destinations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDestinations(response.data.destinations || []);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttributes = async () => {
    try {
      const response = await axios.get(`${API}/admin/destinations/attributes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttributes(response.data);
    } catch (error) {
      console.error('Error fetching attributes:', error);
    }
  };

  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API}/admin/destinations/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersions(response.data.versions || []);
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  };

  const handleFieldEdit = (destId, field, value) => {
    setDestinations(prev => prev.map(d =>
      d.id === destId ? { ...d, [field]: value, status: 'draft' } : d
    ));
    setHasChanges(true);
  };

  const handleMultiSelectEdit = (destId, field, value) => {
    let currentValues = destinations.find(d => d.id === destId)?.[field] || [];
    
    if (currentValues.includes(value)) {
      currentValues = currentValues.filter(v => v !== value);
    } else {
      currentValues = [...currentValues, value];
    }

    handleFieldEdit(destId, field, currentValues);
  };

  const saveDraft = async () => {
    try {
      setLoading(true);
      for (const dest of destinations.filter(d => d.status === 'draft')) {
        await axios.put(`${API}/admin/destinations/${dest.id}`, dest, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      alert('Entwurf erfolgreich gespeichert!');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Fehler beim Speichern des Entwurfs');
    } finally {
      setLoading(false);
    }
  };

  const publishChanges = async () => {
    if (!window.confirm('Möchten Sie alle Änderungen live schalten? Dies erstellt automatisch ein Backup.')) {
      return;
    }

    try {
      setLoading(true);
      await saveDraft();
      await axios.post(`${API}/admin/destinations/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchDestinations();
      await fetchVersions();
      alert('Änderungen erfolgreich live geschaltet!');
      setHasChanges(false);
    } catch (error) {
      console.error('Error publishing changes:', error);
      alert('Fehler beim Live-Schalten der Änderungen');
    } finally {
      setLoading(false);
    }
  };

  const rollbackToVersion = async (versionId) => {
    if (!window.confirm('Möchten Sie wirklich zu dieser Version zurückkehren? Alle aktuellen Änderungen gehen verloren.')) {
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/admin/destinations/rollback/${versionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchDestinations();
      alert('Erfolgreich zur ausgewählten Version zurückgekehrt!');
      setHasChanges(false);
    } catch (error) {
      console.error('Error rolling back:', error);
      alert('Fehler beim Zurückkehren zur Version');
    } finally {
      setLoading(false);
    }
  };

  const deleteDestination = async (destId) => {
    if (!window.confirm('Möchten Sie diese Destination wirklich löschen?')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/admin/destinations/${destId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchDestinations();
      alert('Destination erfolgreich gelöscht!');
    } catch (error) {
      console.error('Error deleting destination:', error);
      alert('Fehler beim Löschen der Destination');
    } finally {
      setLoading(false);
    }
  };

  const addNewDestination = async () => {
    try {
      setLoading(true);
      const newDest = {
        name: "Neue Destination",
        continent: "Europa",
        best_season: ["Mai-Juni"],
        price_category: 3,
        distance: 1,
        climate: ["mediterran & mild"],
        scenarios: ["Strand"],
        cultural_aspects: ["Kulinarik & Wein"],
        ambiance: ["Modern"],
        guest_capacity: ["medium"],
        accommodation_types: ["Hotel"],
        status: "draft"
      };
      await axios.post(`${API}/admin/destinations`, newDest, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchDestinations();
      alert('Neue Destination als Entwurf erstellt!');
    } catch (error) {
      console.error('Error creating destination:', error);
      alert('Fehler beim Erstellen der Destination');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (destId, file) => {
  if (!file) return;
  try {
    setUploadingFor(destId);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'destination');
    formData.append('destination_id', destId);
    const response = await axios.post(`${API}/admin/media/upload`, formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    if (response.data.url) {
      setDestinations(prev => prev.map(d =>
        d.id === destId ? { ...d, image_url: response.data.url } : d
      ));
    }
  } catch (error) {
    console.error('Upload error:', error);
    alert('Fehler beim Hochladen des Bildes');
  } finally {
    setUploadingFor(null);
  }
};
  
  return (
    <div className="destinations-editor">
      <div className="editor-header">
        <h2>Destinationen Editor</h2>
        <div className="editor-actions">
          <button onClick={addNewDestination} className="btn-add-dest" disabled={loading} data-testid="add-destination-btn">
            + Neue Destination
          </button>
          {hasChanges && (
            <>
              <button onClick={saveDraft} className="btn-draft" disabled={loading}>
                <Save size={16} />
                Entwurf speichern
              </button>
              <button onClick={publishChanges} className="btn-publish" disabled={loading}>
                <Upload size={16} />
                Live schalten
              </button>
            </>
          )}
        </div>
      </div>

      <div className="destinations-table-container">
        <table className="destinations-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Kontinent</th>
              <th>Saison</th>
              <th>Preis</th>
              <th>Distanz</th>
              <th>Klima</th>
              <th>Szenarien</th>
              <th>Kultur</th>
              <th>Ambiente</th>
              <th>Gäste</th>
              <th>Status</th>
                <th>Bild</th>
                <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {destinations.map(dest => (
              <tr key={dest.id} className={dest.status === 'draft' ? 'draft-row' : ''}>
                <td>
                  <input
                    type="text"
                    value={dest.name || ''}
                    onChange={(e) => handleFieldEdit(dest.id, 'name', e.target.value)}
                    className="inline-input"
                  />
                </td>
                <td>
                  <select
                    value={dest.continent || ''}
                    onChange={(e) => handleFieldEdit(dest.id, 'continent', e.target.value)}
                    className="inline-select"
                  >
                    <option value="">Wählen...</option>
                    {attributes.continents?.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <div className="multi-select-cell">
                    {editingRow === dest.id + '_season' ? (
                      <div className="multi-select-dropdown">
                        {attributes.seasons?.map(s => (
                          <label key={s} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={dest.best_season?.includes(s)}
                              onChange={() => handleMultiSelectEdit(dest.id, 'best_season', s)}
                            />
                            {s}
                          </label>
                        ))}
                        <button
                          onClick={() => setEditingRow(null)}
                          className="close-dropdown"
                        >
                          Schließen
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingRow(dest.id + '_season')}
                        className="edit-multi-btn"
                      >
                        <Edit2 size={12} />
                        {dest.best_season?.length || 0} ausgewählt
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  <select
                    value={dest.price_category || ''}
                    onChange={(e) => handleFieldEdit(dest.id, 'price_category', parseInt(e.target.value))}
                    className="inline-select"
                  >
                    <option value="">Wählen...</option>
                    {attributes.price_category?.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={dest.distance || ''}
                    onChange={(e) => handleFieldEdit(dest.id, 'distance', parseInt(e.target.value))}
                    className="inline-select"
                  >
                    <option value="">Wählen...</option>
                    {attributes.distance?.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <div className="multi-select-cell">
                    {editingRow === dest.id + '_climate' ? (
                      <div className="multi-select-dropdown">
                        {attributes.climates?.map(c => (
                          <label key={c} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={dest.climate?.includes(c)}
                              onChange={() => handleMultiSelectEdit(dest.id, 'climate', c)}
                            />
                            {c}
                          </label>
                        ))}
                        <button
                          onClick={() => setEditingRow(null)}
                          className="close-dropdown"
                        >
                          Schließen
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingRow(dest.id + '_climate')}
                        className="edit-multi-btn"
                      >
                        <Edit2 size={12} />
                        {dest.climate?.length || 0} ausgewählt
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  <div className="multi-select-cell">
                    {editingRow === dest.id + '_scenarios' ? (
                      <div className="multi-select-dropdown">
                        {attributes.scenarios?.map(s => (
                          <label key={s} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={dest.scenarios?.includes(s)}
                              onChange={() => handleMultiSelectEdit(dest.id, 'scenarios', s)}
                            />
                            {s}
                          </label>
                        ))}
                        <button
                          onClick={() => setEditingRow(null)}
                          className="close-dropdown"
                        >
                          Schließen
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingRow(dest.id + '_scenarios')}
                        className="edit-multi-btn"
                      >
                        <Edit2 size={12} />
                        {dest.scenarios?.length || 0} ausgewählt
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  <div className="multi-select-cell">
                    {editingRow === dest.id + '_cultural' ? (
                      <div className="multi-select-dropdown">
                        {attributes.cultural_aspects?.map(c => (
                          <label key={c} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={dest.cultural_aspects?.includes(c)}
                              onChange={() => handleMultiSelectEdit(dest.id, 'cultural_aspects', c)}
                            />
                            {c}
                          </label>
                        ))}
                        <button
                          onClick={() => setEditingRow(null)}
                          className="close-dropdown"
                        >
                          Schließen
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingRow(dest.id + '_cultural')}
                        className="edit-multi-btn"
                      >
                        <Edit2 size={12} />
                        {dest.cultural_aspects?.length || 0} ausgewählt
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  <div className="multi-select-cell">
                    {editingRow === dest.id + '_ambiance' ? (
                      <div className="multi-select-dropdown">
                        {attributes.ambiance?.map(a => (
                          <label key={a} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={dest.ambiance?.includes(a)}
                              onChange={() => handleMultiSelectEdit(dest.id, 'ambiance', a)}
                            />
                            {a}
                          </label>
                        ))}
                        <button
                          onClick={() => setEditingRow(null)}
                          className="close-dropdown"
                        >
                          Schließen
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingRow(dest.id + '_ambiance')}
                        className="edit-multi-btn"
                      >
                        <Edit2 size={12} />
                        {dest.ambiance?.length || 0} ausgewählt
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  <div className="multi-select-cell">
                    {editingRow === dest.id + '_guests' ? (
                      <div className="multi-select-dropdown">
                        {attributes.guest_capacity?.map(g => (
                          <label key={g} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={dest.guest_capacity?.includes(g)}
                              onChange={() => handleMultiSelectEdit(dest.id, 'guest_capacity', g)}
                            />
                            {g}
                          </label>
                        ))}
                        <button
                          onClick={() => setEditingRow(null)}
                          className="close-dropdown"
                        >
                          Schließen
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingRow(dest.id + '_guests')}
                        className="edit-multi-btn"
                      >
                        <Edit2 size={12} />
                        {dest.guest_capacity?.length || 0} ausgewählt
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${dest.status || 'live'}`}>
                    {dest.status || 'live'}
                  </span>
                 </td>
                <td>
  <div className="dest-image-cell">
    {dest.image_url && (
      <img src={dest.image_url} alt={dest.name} className="dest-thumb" />
    )}
    <label className="btn-upload-img" data-testid={`upload-img-${dest.id}`}>
      {uploadingFor === dest.id ? (
        <span className="upload-spinner">...</span>
      ) : (
        <>
          <Upload size={14} />
          {dest.image_url ? 'Ändern' : 'Hochladen'}
        </>
      )}
      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => handleImageUpload(dest.id, e.target.files[0])}
        disabled={uploadingFor === dest.id}
      />
    </label>
  </div>
</td>
                <td>
                  <button
                    onClick={() => deleteDestination(dest.id)}
                    className="btn-delete"
                    disabled={loading}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="versions-panel">
        <h3>Versionen ({versions.length})</h3>
        <div className="versions-list">
          {versions.map(version => (
            <div key={version.id} className="version-item">
              <div className="version-info">
                <strong>{version.version_name}</strong>
                <span className="version-date">
                  {new Date(version.created_at).toLocaleString('de-DE')}
                </span>
              </div>
              <button
                onClick={() => rollbackToVersion(version.id)}
                className="btn-rollback"
                disabled={loading}
              >
                <RotateCcw size={14} />
                Wiederherstellen
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DestinationsEditor;
